import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { QRCodeSVG } from 'qrcode.react'
import { ClockIcon } from '@heroicons/react/24/outline'
import LoadingSpinner from '@/components/LoadingSpinner'
import { useRewardCoupons } from '@/hooks/useRewards'
import type { RewardCoupon } from '@/types'

// ---------------------------------------------------------------------------
// Per-coupon rotating QR panel
// ---------------------------------------------------------------------------

interface QrPanelProps {
  coupon: RewardCoupon
  onExpire: () => void
}

function QrPanel({ coupon, onExpire }: QrPanelProps) {
  const { t } = useTranslation('rewards')
  const [secondsLeft, setSecondsLeft] = useState(coupon.expires_in_seconds)
  const [prevExpiry, setPrevExpiry] = useState(coupon.expires_in_seconds)

  // Reset the countdown when the coupon's expiry changes — adjusting state
  // during render (React-recommended) rather than synchronously in an effect.
  if (prevExpiry !== coupon.expires_in_seconds) {
    setPrevExpiry(coupon.expires_in_seconds)
    setSecondsLeft(coupon.expires_in_seconds)
  }

  useEffect(() => {
    if (secondsLeft <= 0) {
      onExpire()
      return
    }
    const id = window.setTimeout(() => setSecondsLeft((s) => s - 1), 1000)
    return () => window.clearTimeout(id)
  }, [secondsLeft, onExpire])

  return (
    <div className="flex flex-col items-center gap-4 w-full">
      <p className="text-lg font-bold text-base-content text-center">{coupon.campaign_name}</p>
      <p className="text-sm text-base-content/60 text-center">{t('campaign_qr')}</p>

      {/* Large QR code */}
      <div className="p-4 bg-white rounded-2xl shadow-md border border-base-200">
        <QRCodeSVG value={coupon.qr_payload} size={240} level="M" />
      </div>

      {/* Countdown bar */}
      <div className="w-full max-w-[280px] space-y-1">
        <div className="flex items-center justify-center gap-1 text-sm text-base-content/70">
          <ClockIcon className="w-4 h-4" />
          <span>{t('qr_expires_in', { seconds: Math.max(0, secondsLeft) })}</span>
        </div>
        <div className="w-full h-1.5 bg-base-200 rounded-full overflow-hidden">
          <div
            className="h-full bg-primary rounded-full transition-all duration-1000"
            style={{
              width: `${Math.max(0, (secondsLeft / coupon.period_seconds) * 100)}%`,
            }}
          />
        </div>
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export default function RewardQrPage() {
  const { t } = useTranslation('rewards')
  const { t: tc } = useTranslation('common')
  const { data: coupons, isLoading, error, refetch, isFetching } = useRewardCoupons()
  const [selectedIndex, setSelectedIndex] = useState(0)

  // When coupons refresh (after expire), keep selection in bounds — derived
  // during render so we don't sync state in an effect.
  const safeIndex = coupons && selectedIndex < coupons.length ? selectedIndex : 0

  const handleExpire = () => {
    void refetch()
  }

  if (isLoading) {
    return <LoadingSpinner />
  }

  if (error) {
    return (
      <div className="p-4">
        <div className="alert alert-error">{tc('errors.something_went_wrong')}</div>
      </div>
    )
  }

  if (!coupons || coupons.length === 0) {
    return (
      <div className="p-4 text-center py-16 space-y-3">
        <p className="text-sm text-base-content/60">{t('no_coupons')}</p>
        <p className="text-xs text-base-content/40">{t('no_coupons_sub')}</p>
      </div>
    )
  }

  const currentCoupon = coupons[safeIndex] as RewardCoupon

  return (
    <div className="p-4 space-y-6 flex flex-col items-center">
      {/* Campaign selector (only shown when multiple campaigns) */}
      {coupons.length > 1 && (
        <div className="w-full">
          <p className="text-xs font-semibold text-base-content/60 mb-2 uppercase tracking-wide">
            {t('select_campaign')}
          </p>
          <div className="flex bg-base-200 rounded-full p-1 gap-1">
            {coupons.map((coupon, index) => (
              <button
                key={coupon.coupon_id}
                className={`flex-1 py-1.5 text-xs font-medium rounded-full truncate transition-all ${
                  safeIndex === index
                    ? 'bg-base-100 text-base-content shadow-sm'
                    : 'text-base-content/60'
                }`}
                onClick={() => setSelectedIndex(index)}
              >
                {coupon.campaign_name}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* QR panel — re-mount on coupon change so timer resets cleanly */}
      {isFetching ? (
        <div className="py-8">
          <LoadingSpinner />
        </div>
      ) : (
        <QrPanel
          key={`${currentCoupon.coupon_id}-${currentCoupon.totp_code}`}
          coupon={currentCoupon}
          onExpire={handleExpire}
        />
      )}
    </div>
  )
}
